import React, { FC, useEffect, useMemo } from 'react';
import { Box, Fade, keyframes, Paper, Portal, Typography } from '@mui/material';
import { FallingSnow } from './FallingSnow';
import { ChristmasTree } from './ChristmasTree';
import { ChristmasColors } from './christmasColors';

export const GREETINGS = [
  { title: 'God jul! 🎄', text: 'Vi i Museum24-teamet ønsker dere en riktig god jul og et godt nytt år.' },
  { title: 'Ho ho ho! 🎅', text: 'Juletreet ønsker deg en fredelig og fin julestund.' },
  { title: 'Du fant juletreet!', text: 'Tre klikk, og snøen laver ned over Museum24. God jul!' },
  { title: 'Julehilsen fra Museum24', text: 'Ta en kopp gløgg, se på snøen som faller, og publiser noe fint.' },
  { title: 'Juletreet sier hei', text: 'Visste du at juletreet kom til Norge på 1820-tallet? Nå vet du det.' },
];

let timesShown = 0;

/** The first greeting is always shown the first time the easter egg opens; after that one is picked at random. */
export const pickGreeting = (): (typeof GREETINGS)[number] => {
  const greeting = timesShown === 0 ? GREETINGS[0] : GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
  timesShown += 1;
  return greeting;
};

/** Test helper so every test starts with the first greeting. */
export const resetGreetings = (): void => {
  timesShown = 0;
};

const sway = keyframes`
  0%, 100% { transform: rotate(-4deg) scale(1); }
  50% { transform: rotate(4deg) scale(1.06); }
`;

export const EASTER_EGG_DURATION_MS = 8000;

export interface ChristmasEasterEggProps {
  onDone: () => void;
  /** Override for tests. */
  durationMs?: number;
}

/** Full-screen falling snow and a greeting card. Click anywhere on the card, or wait, to dismiss. */
export const ChristmasEasterEgg: FC<ChristmasEasterEggProps> = ({ onDone, durationMs = EASTER_EGG_DURATION_MS }) => {
  const greeting = useMemo(pickGreeting, []);

  useEffect(() => {
    const timeout = window.setTimeout(onDone, durationMs);
    return () => window.clearTimeout(timeout);
  }, [onDone, durationMs]);

  return (
    <Portal>
      <FallingSnow count={60} minDuration={5} maxDuration={9} zIndex={2000} />
      <Fade in appear timeout={600}>
        <Box
          data-testid='ChristmasEasterEgg'
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2001,
            pointerEvents: 'none',
          }}>
          <Paper
            elevation={8}
            role='status'
            onClick={onDone}
            sx={{
              pointerEvents: 'auto',
              cursor: 'pointer',
              px: 4,
              py: 3,
              maxWidth: 420,
              textAlign: 'center',
              borderRadius: 3,
              border: `2px solid ${ChristmasColors.TREE}`,
              background: `linear-gradient(180deg, #fff 0%, ${ChristmasColors.HEADER_TINT} 100%)`,
            }}>
            <Box
              sx={{
                display: 'inline-block',
                animation: `${sway} 1.4s ease-in-out infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}>
              <ChristmasTree size={80} lit />
            </Box>
            <Typography variant='h6' sx={{ mt: 1, fontWeight: 700, color: ChristmasColors.TREE_DARK }}>
              {greeting.title}
            </Typography>
            <Typography variant='body1' sx={{ mt: 0.5 }}>
              {greeting.text}
            </Typography>
            <Typography variant='caption' sx={{ display: 'block', mt: 1.5, opacity: 0.6 }}>
              Klikk for å lukke
            </Typography>
          </Paper>
        </Box>
      </Fade>
    </Portal>
  );
};

export default ChristmasEasterEgg;
